import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { loadOfferHeader } from 'actions/actionCreators/offer/offerActions';
import { getInt } from 'utils/functions';
import { showError } from 'actions/actionCreators/snackbarActions';
import { getFullOfferHeader } from './selectors';
import OfferType from 'constants/offerType';
import documentTypeIds from 'constants/documentTypeIds';
import { loadOfferTypes } from 'actions/actionCreators/dictionariesActions';
import { getDocumentSubtypes } from 'selectors/dictionariesSelectors';
import { loadDocumentSecurity } from 'features/document/edit/documents/actionCreators';
import OfferStepperContainer from './OfferStepperContainer';

const OfferWizardRouteContainer = props => {
  const { match, location, history, offerHeader, offerTypes, actions } = props;

  const documentId = getInt(match.params.documentId);
  const siteId = getInt(match.params.siteId);
  const isNewOffer = match.params.newOffer === 'new';
  const isNewContractModification = match.params.newRestructuring === 'restructuring';
  const isNewFacilityFrame = match.params.newFacilityFrame === 'facilityFrame';

  const [accessResult, setAccessResult] = useState(null);

  const createOfferHeader = () => {
    let offerHeaderTmp = offerHeader;

    if (isNewOffer) {
      offerHeaderTmp.offerTypeId = OfferType.newRequest.id;
      offerHeaderTmp.documentTypeId = documentTypeIds.Offer;
    } else if (isNewContractModification) {
      offerHeaderTmp.offerTypeId = OfferType.restructuring.id;
      offerHeaderTmp.documentTypeId = documentTypeIds.OfferModification;
    } else if (isNewFacilityFrame) {
      offerHeaderTmp.documentTypeId = documentTypeIds.FacilityFrameOffer;
      const oTypeId = offerTypes.filter(o => o.implicit === true && o.forFacility === true)?.offerTypeId;
      offerHeaderTmp.offerTypeId = oTypeId ?? OfferType.facilityOffer.id;
    }

    return offerHeaderTmp;
  };

  const loadData = useCallback(() => {
    actions.loadOfferHeader(documentId, siteId);
  }, [actions, documentId, siteId]);

  useEffect(() => {
    if (isNewOffer || isNewContractModification) return;

    if (isNewFacilityFrame) {
      actions.loadOfferTypes(documentTypeIds.FacilityFrameOffer);
      return;
    }

    const getAccessResult = async () => {
      const accessResultTemp = await actions.loadDocumentSecurity(documentId, siteId);
      setAccessResult(accessResultTemp);
    };

    getAccessResult();
  }, [isNewContractModification, isNewFacilityFrame, isNewOffer, documentId, siteId, actions]);

  useEffect(() => {
    if (accessResult === null) return;

    if (accessResult.access) loadData();
    else history.push('/notAllowed');
  }, [accessResult, loadData, history]);

  useEffect(() => {
    if (documentId == null || siteId == null) return;

    if (accessResult === null || accessResult.access === false) return;

    if (!offerHeader?.isInvalid) return;

    loadData();
  }, [documentId, siteId, accessResult, offerHeader?.isInvalid, loadData]);

  return (
    (offerHeader.documentId || isNewOffer || isNewContractModification || isNewFacilityFrame) && (
      <OfferStepperContainer
        documentId={documentId}
        siteId={siteId}
        newOffer={match.params.newOffer ?? match.params.newRestructuring ?? match.params.newFacilityFrame}
        offerHeader={createOfferHeader()}
        locationPathname={location.pathname}
        history={history}
      />
    )
  );
};

function mapStateToProps(state) {
  const offerHeader = getFullOfferHeader(state.currentOffer.header, state.entities.offerPartners);
  const offerTypes = getDocumentSubtypes(state, offerHeader.documentTypeId);
  return { offerHeader, offerTypes };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ loadOfferHeader, showError, loadOfferTypes, loadDocumentSecurity }, dispatch),
    dispatch
  };
}

OfferWizardRouteContainer.propTypes = {
  match: PropTypes.object.isRequired,
  location: PropTypes.object,
  history: PropTypes.object.isRequired,
  offerHeader: PropTypes.object,
  actions: PropTypes.object.isRequired,
  offerTypes: PropTypes.array
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(withRouter(OfferWizardRouteContainer));
